import React, { useMemo } from "react";

import { UnitFormatsProvider } from "@strava/units/react";

import { Map, PolylineHighlightSectionType } from "@strava/feature-global-wasm-mre";

import startCropMarker from './assets/map-marker-range-start.png';
import endCropMarker from './assets/map-marker-range-end.png';

import I18n from "../../utils/I18n";

import StravaPackageContextProviders from "../../apps/StravaPackageContextProviders";

// Stylesheets
import styles from "./RangeSelectMapWithMRE.scss";

// TODO: Remove this when PrivacyZone type is exported from @strava/feature-global-wasm-mre (https://strava.atlassian.net/browse/WRE-760)
export interface PrivacyZone {
  id: string;
  position: {
    lat: number;
    lng: number;
  };
  radius: number;
}
interface RangeSelectMapWithMREProps {
  activityId: number;
  polyline: [number, number][];
  privacyZones?: PrivacyZone[];
  startPointIndex?: number;
  endPointIndex?: number | null;
}

export const RangeSelectMapWithMRE = ({
  activityId,
  polyline = [],
  privacyZones = [],
  startPointIndex,
  endPointIndex
}: RangeSelectMapWithMREProps) => {
  const highlights = useMemo(() => {
    return [
      ...(startPointIndex && startPointIndex > 0
        ? [
            {
              startIndex: BigInt(0), // Start of the activity
              endIndex: BigInt(startPointIndex), // Up to the start point
              identifier: `${activityId}-startPoint-highlight`,
              type: PolylineHighlightSectionType.BLUE
            }
          ]
        : []),
      ...(endPointIndex && endPointIndex < polyline.length - 1
        ? [
            {
              startIndex: BigInt(endPointIndex), // From the end point
              endIndex: BigInt(polyline.length - 1), // To the end of the activity
              identifier: `${activityId}-endPoint-highlight`,
              type: PolylineHighlightSectionType.BLUE
            }
          ]
        : [])
    ];
  }, [activityId, startPointIndex, endPointIndex, polyline]);

  return (
    <div className={styles.map}>
      <StravaPackageContextProviders>
        <UnitFormatsProvider
          locale={I18n.language()}
          unitSystem={I18n.measurementUnits()}
        >
          <Map
            polyline={polyline}
            showPOIs={true}
            page={process.env.ACTIVITY_DETAILS_ANALYTICS_PAGE || ""}
            category={process.env.ACTIVITY_DETAILS_ANALYTICS_CATEGORY || ""}
            surfaceIdentifier={process.env.ACTIVITY_DETAILS_SURFACE_IDENTIFIER}
            mapboxToken={process.env.ACTIVITY_DETAILS_MAPBOX_TOKEN}
          >
            <Map.UIControls />

            {/* Render privacy zones if any are provided */}
            {privacyZones && <Map.PrivacyZones privacyZones={privacyZones} />}

            {/* Render disclaimer popups for each privacy zone */}
            {privacyZones?.map(({ id, position: { lat, lng } }) => (
              <Map.Popup latLng={[lat, lng]} key={id} isDismissible={true}>
                {I18n.t("mre.private_location_leaderboard_disclaimer")}
              </Map.Popup>
            ))}

            <Map.Activity
              id={activityId.toString()}
              polyline={polyline}
              highlights={highlights}
            />

            {/* Start Point Marker */}
            {startPointIndex && (
              <Map.PlaceHTMLOnMap latLng={polyline[startPointIndex]} className={styles.cropMarker}>
                <img src={startCropMarker} alt={I18n.t("mre.crop_markers.start_marker")} />
              </Map.PlaceHTMLOnMap>
            )}

            {/* End Point Marker */}
            {endPointIndex && (
              <Map.PlaceHTMLOnMap latLng={polyline[endPointIndex]} className={styles.cropMarker}>
                <img src={endCropMarker} alt={I18n.t("mre.crop_markers.end_marker")} />
              </Map.PlaceHTMLOnMap>
            )}
          </Map>
        </UnitFormatsProvider>
      </StravaPackageContextProviders>
    </div>
  );
};

export default RangeSelectMapWithMRE;
